<template>
  <div class="license-page">
    <div class="page-head">
      <div>
        <div class="page-title">会员套餐</div>
        <div class="page-subtitle">配置 plan 与 feature_keys 的关系，客户端授权 payload 会读取这里的结果。</div>
      </div>
      <el-button type="primary" icon="el-icon-plus" @click="createPlan">新增套餐</el-button>
    </div>

    <div class="filter-bar">
      <el-input v-model="search.keyword" clearable placeholder="搜索 plan key / 套餐名称" @change="readList">
        <template slot="prepend">关键词</template>
      </el-input>
      <el-select v-model="search.enabled" clearable placeholder="启用状态" @change="readList">
        <el-option label="启用" :value="1" />
        <el-option label="禁用" :value="0" />
      </el-select>
      <el-button icon="el-icon-refresh" @click="readList">刷新</el-button>
    </div>

    <el-table v-loading="loading" :data="list" border class="license-table">
      <el-table-column prop="plan_key" label="套餐 key" min-width="150" />
      <el-table-column prop="plan_name" label="套餐名称" min-width="150" />
      <el-table-column prop="feature_count" label="功能数" width="90" />
      <el-table-column prop="enabled" label="状态" width="90">
        <template slot-scope="scope">
          <el-tag size="mini" :type="Number(scope.row.enabled) === 1 ? 'success' : 'info'">
            {{ Number(scope.row.enabled) === 1 ? '启用' : '禁用' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="is_system" label="系统套餐" width="100">
        <template slot-scope="scope">
          <el-tag size="mini" :type="Number(scope.row.is_system) === 1 ? 'warning' : 'info'">
            {{ Number(scope.row.is_system) === 1 ? '是' : '否' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="sort" label="排序" width="80" />
      <el-table-column prop="description" label="说明" min-width="260" show-overflow-tooltip />
      <el-table-column label="操作" width="220" fixed="right">
        <template slot-scope="scope">
          <el-button size="mini" icon="el-icon-edit" @click="editPlan(scope.row)">编辑</el-button>
          <el-button size="mini" icon="el-icon-copy-document" @click="openCopyDialog(scope.row)">复制</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      class="pager"
      :current-page="page_num"
      :page-size="page_size"
      :page-sizes="[10, 20, 50, 100]"
      layout="total, sizes, prev, pager, next, jumper"
      :total="total"
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
    />

    <el-dialog title="复制套餐" :visible.sync="copyDialogVisible" width="520px" :append-to-body="true">
      <el-form :model="copyForm" label-width="110px">
        <el-form-item label="来源套餐">
          <el-input :value="copySourceName" disabled />
        </el-form-item>
        <el-form-item label="新套餐 key">
          <el-input v-model="copyForm.plan_key" placeholder="例如 pro_plus" />
        </el-form-item>
        <el-form-item label="新套餐名称">
          <el-input v-model="copyForm.plan_name" placeholder="例如 Pro Plus会员" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="copyDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="copySaving" @click="copyPlan">复制</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import apis from '@/api/index'

export default {
  name: 'LicensePlans',
  data() {
    return {
      loading: false,
      copySaving: false,
      copyDialogVisible: false,
      page_num: 1,
      page_size: 20,
      total: 0,
      search: {
        keyword: '',
        enabled: '',
      },
      list: [],
      copySourceName: '',
      copyForm: {
        id: 0,
        plan_key: '',
        plan_name: '',
      },
    }
  },
  methods: {
    // 读取套餐列表，并显示每个套餐当前启用的功能数量。
    readList(resetPage = true) {
      if (resetPage) this.page_num = 1
      this.loading = true
      const params = Object.assign({}, this.search, {
        page_num: this.page_num,
        page_size: this.page_size,
      })
      apis.license.readPlans(params).then((res) => {
        const data = res.data && res.data.data ? res.data.data : {}
        this.list = data.list || []
        this.total = Number(data.count || 0)
      }).catch((err) => {
        this.$message({ message: String(err || '读取套餐失败'), type: 'error' })
      }).finally(() => {
        this.loading = false
      })
    },
    // 跳转新增页，新增页会加载全部 feature 供勾选。
    createPlan() {
      this.$router.push({ name: 'licensePlanEdit' })
    },
    // 跳转编辑页，用 query 传 id，刷新页面也能恢复。
    editPlan(row) {
      this.$router.push({ name: 'licensePlanEdit', query: { id: row.id } })
    },
    // 打开复制弹窗，默认生成一个容易识别的新 key。
    openCopyDialog(row) {
      this.copySourceName = row.plan_name + ' (' + row.plan_key + ')'
      this.copyForm = {
        id: row.id,
        plan_key: row.plan_key + '_copy',
        plan_name: row.plan_name + ' 副本',
      }
      this.copyDialogVisible = true
    },
    // 调用服务端复制套餐，复制后跳转到编辑页继续细调。
    copyPlan() {
      this.copySaving = true
      apis.license.copyPlan(this.copyForm).then((res) => {
        const body = res.data || {}
        if (body.code !== 1) {
          this.$message({ message: body.msg || '复制失败', type: 'error' })
          return
        }
        this.$message({ message: '复制成功', type: 'success' })
        this.copyDialogVisible = false
        const id = body.data && body.data.id ? body.data.id : 0
        this.$router.push({ name: 'licensePlanEdit', query: { id } })
      }).catch((err) => {
        this.$message({ message: String(err || '复制失败'), type: 'error' })
      }).finally(() => {
        this.copySaving = false
      })
    },
    // 每页数量变化时重新查询第一页。
    handleSizeChange(size) {
      this.page_size = size
      this.readList(true)
    },
    // 页码变化时保持当前筛选条件。
    handleCurrentChange(page) {
      this.page_num = page
      this.readList(false)
    },
  },
  activated() {
    this.readList(false)
  },
  mounted() {
    this.readList()
  },
}
</script>

<style lang="scss" scoped>
.license-page {
  padding: 18px;
  background: #f5f7fa;
  min-height: calc(100vh - 36px);
}
.page-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.page-title {
  font-size: 24px;
  color: #1f2d3d;
  line-height: 32px;
}
.page-subtitle {
  color: #6b7280;
  font-size: 13px;
  margin-top: 4px;
}
.filter-bar {
  display: grid;
  grid-template-columns: minmax(280px, 420px) 160px 96px;
  gap: 10px;
  align-items: center;
  margin-bottom: 12px;
}
.license-table {
  background: #fff;
}
.pager {
  margin-top: 14px;
  text-align: right;
}
</style>
